/** The main thread's handle on the engine in the worker — phase 3.
 *
 * ⚠ One worker per tab, not per drawing: the engine holds ONE store (`STORE` is a thread-local
 * in `pdcad-wasm`), and opening a second drawing replaces the first. That is the same shape as
 * the studio, which shows one drawing at a time.
 *
 * ⚠⚠ **A REFUSAL IS NOT A FAILURE, AND THE DIFFERENCE IS THE WHOLE FALLBACK STRATEGY.** The
 * engine answers `rc` 1 for a case it does not reproduce; the caller then asks the server, which
 * is precisely what `display_rs` does on the other side of the wall. `call` hands both back;
 * `must` raises on either, for the callers that have nowhere to fall back to.
 */
import type { LocalRequest, LocalResponse } from "./worker";

export interface LocalAnswer {
  rc: 0 | 1 | 2;
  text: string;
  ms: number;
  heap: number;
}

export class LocalEngineError extends Error {
  /** ⚠ See `abi.ts`: a constructor parameter property is not strippable TypeScript. */
  readonly call: string;
  constructor(call: string, message: string) {
    super(`${call}: ${message}`);
    this.name = "LocalEngineError";
    this.call = call;
  }
}

/** Where the module is served from. ⚠ `rust/install.sh` copies it into `frontend/public/`, which
 *  vite serves in dev and copies verbatim into `dist/`. It is NOT in git: 2.2 MB of build
 *  output, and the repo is on GitHub. */
export const WASM_URL = "/pdcad_wasm.wasm";

export class LocalEngine {
  private worker: Worker;
  private seq = 0;
  private readonly waiting = new Map<number, {
    resolve: (v: LocalResponse) => void; reject: (e: Error) => void;
  }>();
  private booted: Promise<void> | null = null;
  /** The last call's heap, so a caller can show it without asking again. */
  heap = 0;

  /** ⚠ `make` is how a caller supplies its own Worker — a harness outside a browser, or a Tauri
   *  window. It is a FACTORY and not an instance because `recycle()` has to be able to make
   *  another one: an injected worker that could not be replaced would silently become a real
   *  `new Worker(...)` the first time a big drawing recycled the module. */
  constructor(make?: () => Worker) {
    // ⚠ `new URL(..., import.meta.url)` is the form vite understands — it bundles the worker as
    // its own chunk. A string path would be left alone and 404 in `dist/`.
    this.make = make
      ?? (() => new Worker(new URL("./worker.ts", import.meta.url), { type: "module" }));
    this.worker = this.make();
    this.listen();
  }

  private readonly make: () => Worker;

  private listen(): void {
    this.worker.onmessage = (ev: MessageEvent<LocalResponse>) => {
      const w = this.waiting.get(ev.data.id);
      if (!w) return;
      this.waiting.delete(ev.data.id);
      if (typeof ev.data.heap === "number") this.heap = ev.data.heap;
      w.resolve(ev.data);
    };
    // ⚠ A worker that dies takes every pending promise with it. Without this they never settle
    // and the studio waits for ever with no error — the failure mode that reads as a hang.
    this.worker.onerror = (ev: ErrorEvent) => {
      const why = ev.message || "the engine worker stopped";
      for (const [, w] of this.waiting) w.reject(new LocalEngineError("worker", why));
      this.waiting.clear();
    };
  }

  private send(req: Omit<LocalRequest, "id">): Promise<LocalResponse> {
    const id = ++this.seq;
    return new Promise<LocalResponse>((resolve, reject) => {
      this.waiting.set(id, { resolve, reject });
      this.worker.postMessage({ ...req, id } satisfies LocalRequest);
    });
  }

  /** Load the module. ⚠ Idempotent and shared: two callers racing to open a drawing must not
   *  instantiate it twice — 2.2 MB compiled twice is a second nobody asked for. */
  boot(url: string = WASM_URL): Promise<void> {
    if (this.booted === null) {
      this.booted = this.send({ name: "boot", url }).then((r) => {
        if (!r.ok) {
          this.booted = null;                      // ⚠ so a later attempt can retry
          throw new LocalEngineError("boot", r.error || "the engine would not load");
        }
      });
    }
    return this.booted;
  }

  /** Is local mode available at all? ⚠ Answers rather than throws: a server with no module is a
   *  normal deployment, not an error, and the studio simply stays in server mode. */
  async available(url: string = WASM_URL): Promise<boolean> {
    try {
      await this.boot(url);
      return true;
    } catch {
      return false;
    }
  }

  async call(name: string, args: readonly string[] = []): Promise<LocalAnswer> {
    await this.boot();
    const r = await this.send({ name, args: [...args] });
    if (!r.ok) throw new LocalEngineError(name, r.error || "the engine failed");
    return { rc: r.rc ?? 2, text: r.text ?? "", ms: r.ms ?? 0, heap: r.heap ?? 0 };
  }

  /** A call carrying BYTES — a .dwg the drafter picked.
   *
   * ⚠⚠ **THE BUFFER IS TRANSFERRED, WHICH DETACHES IT HERE.** That is the point: a 48 MB
   * drawing must not be copied into the worker on top of the copy the module makes. The caller
   * must not read `bytes` again afterwards — it is gone, by design, and a `byteLength` of 0 is
   * what a caller that forgot will see.
   */
  async callBytes(name: string, bytes: ArrayBuffer,
                  args: readonly string[] = []): Promise<LocalAnswer> {
    await this.boot();
    const id = ++this.seq;
    const r = await new Promise<LocalResponse>((resolve, reject) => {
      this.waiting.set(id, { resolve, reject });
      this.worker.postMessage({ id, name, args: [...args], bytes } satisfies LocalRequest,
                              [bytes]);
    });
    if (!r.ok) throw new LocalEngineError(name, r.error || "the engine failed");
    return { rc: r.rc ?? 2, text: r.text ?? "", ms: r.ms ?? 0, heap: r.heap ?? 0 };
  }

  /** The answer, with a refusal raised too. */
  /** A call whose answer is a FILE. The engine is lent the DWG template by the worker, so the
   *  caller passes nothing but the arguments. Returns the report it prefixes and the bytes.
   *
   * ⚠ The answer is `<report length>\n<report JSON><the file>` — the report cannot go in a
   * second call because the writer builds the file and the report in one pass, and it matters:
   * what this writer cannot build yet it COUNTS, so a drafter can be told what a save left out
   * rather than handed a file that is quietly short. */
  async callForBytes(name: string, args: readonly string[] = []):
      Promise<{ report: unknown; bytes: Uint8Array; ms: number }> {
    await this.boot();
    const id = ++this.seq;
    const r = await new Promise<LocalResponse>((resolve, reject) => {
      this.waiting.set(id, { resolve, reject });
      this.worker.postMessage({ id, name, args: [...args], wantsBytes: true } satisfies LocalRequest);
    });
    if (!r.ok) throw new LocalEngineError(name, r.error || "the engine failed");
    if (r.rc !== 0 || !r.raw) throw new LocalEngineError(name, r.text || "the engine failed");
    const all = new Uint8Array(r.raw);
    const nl = all.indexOf(10);
    if (nl < 0) throw new LocalEngineError(name, "the answer carries no report");
    const len = Number(new TextDecoder().decode(all.subarray(0, nl)));
    const head = nl + 1;
    // ⚠ `slice`, not `subarray`: the caller hands these bytes to a file writer, and a view over
    // the whole answer would write the report into the drawing. That defect has been paid for
    // once already in `files.ts`.
    return { report: JSON.parse(new TextDecoder().decode(all.subarray(head, head + len))),
             bytes: all.slice(head + len), ms: 0 };
  }

  async must(name: string, args: readonly string[] = []): Promise<string> {
    const a = await this.call(name, args);
    if (a.rc !== 0) throw new LocalEngineError(name, a.text);
    return a.text;
  }

  async json<T>(name: string, args: readonly string[] = []): Promise<T> {
    return JSON.parse(await this.must(name, args)) as T;
  }

  /** Let the drawing go. ⚠ `store.close` frees the rows inside the module; dropping the worker
   *  would too, but only when the browser gets round to it, and a 492 MB heap is worth asking
   *  for back. */
  async close(): Promise<void> {
    if (this.booted === null) return;
    try {
      await this.call("store.close");
    } catch {
      /* closing a store that is not open is not a failure worth reporting */
    }
  }

  terminate(): void {
    this.worker.terminate();
    this.waiting.clear();
    this.booted = null;
  }

  /** Throw the module away and start a new one.
   *
   * ⚠⚠ **A BIG DRAWING LEAVES THE HEAP BIG, AND `store.close` DOES NOT GIVE IT BACK.** Rust's
   * allocator keeps the pages it has grown into; wasm32's address space is 4 GB; and reading a
   * 48 MB .dwg costs ~2.8 GB (measured — `qa/wasm_dwg.py`). So the SECOND big drawing in one
   * module runs out of memory inside the reader, while exactly the same file opens perfectly in
   * a fresh one. A worker is cheap and a drafter's second drawing is not optional.
   *
   * ⚠ Every pending call is rejected first: a promise waiting on a worker that no longer exists
   * never settles, which is the failure mode that reads as a hang.
   */
  recycle(): void {
    for (const [, w] of this.waiting) {
      w.reject(new LocalEngineError("worker", "the engine was restarted for a new drawing"));
    }
    const old = this.worker;
    this.waiting.clear();
    this.booted = null;
    this.heap = 0;
    this.worker = this.make();
    this.listen();
    old.terminate();
  }

  /** How much of the module's heap is committed. ⚠ The number `recycle()` exists for: past about
   *  3 GB the next big drawing will not fit in wasm32's 4 GB. */
  heapBytes(): number {
    return this.heap;
  }
}

/** The tab's engine. ⚠ Created on FIRST USE, not at module load: a drafter who never opens a
 *  local drawing should not pay for a worker, and `import`ing this file must stay free. */
let shared: LocalEngine | null = null;

export function localEngine(): LocalEngine {
  if (shared === null) shared = new LocalEngine();
  return shared;
}

export function resetLocalEngine(): void {
  shared?.terminate();
  shared = null;
}
